/**
 * The organization's own settings, as bands on the tenant home: the record
 * (name, description, contact), the task-concurrency ceiling, and the
 * webhook. These came whole from the retired organization record page;
 * Feature 70 module 4 decides whether any becomes a panel form.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@optum/api-client/client';
import {
  setOrgTaskConcurrency,
  updateOrganization,
} from '@optum/api-client/endpoints/organizations';
import type { AdminOrganization } from '@optum/api-client/types';

import { Band } from '../../ui/Band.js';
import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Input } from '../../ui/finixt.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { WebhookCard } from '../webhooks/WebhookCard.js';

export function OrgSettings({
  org,
  canMutate,
  partnerName,
}: {
  org: AdminOrganization;
  canMutate: boolean;
  /** The partner whose endpoint receives deliveries while this tenant has none. */
  partnerName: string | null;
}) {
  return (
    <section aria-label="Settings" style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <Band label="Settings" style={{ marginTop: 30 }} />
      <SectionCard title="Record" subtitle="What the tenant is called and who to contact.">
        <SectionCardBody>
          <EditOrgForm
            key={org.updated_at}
            id={org.id}
            name={org.name}
            description={org.description ?? ''}
            contactEmail={org.contact_email ?? ''}
            canMutate={canMutate}
          />
        </SectionCardBody>
      </SectionCard>
      <SectionCard
        title="Task concurrency"
        subtitle="How many of this tenant's tasks may run at once, across all its agents."
      >
        <SectionCardBody>
          <TaskConcurrencyForm
            key={org.max_concurrent_tasks ?? 'default'}
            id={org.id}
            current={org.max_concurrent_tasks}
            canMutate={canMutate}
          />
        </SectionCardBody>
      </SectionCard>
      <SectionCard title="Webhook" subtitle="Where this tenant's task output is delivered.">
        <SectionCardBody>
          <WebhookCard owner={{ kind: 'organization', id: org.id }} fallsThroughTo={partnerName} />
        </SectionCardBody>
      </SectionCard>
    </section>
  );
}

function EditOrgForm({
  id,
  name,
  description,
  contactEmail,
  canMutate,
}: {
  id: string;
  name: string;
  description: string;
  contactEmail: string;
  canMutate: boolean;
}) {
  const [nextName, setNextName] = useState(name);
  const [nextDesc, setNextDesc] = useState(description);
  const [nextEmail, setNextEmail] = useState(contactEmail);
  const qc = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: () =>
      updateOrganization(id, {
        name: nextName,
        description: nextDesc || null,
        contact_email: nextEmail || null,
      }),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: qk.org(id) });
      qc.invalidateQueries({ queryKey: qk.orgs() });
    },
    onError: (err) =>
      toast.error('Save failed', err instanceof ApiError ? err.message : String(err)),
  });

  function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    mutation.mutate();
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <Field id="org-name" label="Display name" required>
        <Input
          id="org-name"
          value={nextName}
          onChange={(e) => setNextName(e.target.value)}
          required
          disabled={!canMutate}
        />
      </Field>
      <Field id="org-desc" label="Description">
        <Input
          id="org-desc"
          value={nextDesc}
          onChange={(e) => setNextDesc(e.target.value)}
          disabled={!canMutate}
        />
      </Field>
      <Field id="org-email" label="Contact email">
        <Input
          id="org-email"
          type="email"
          value={nextEmail}
          onChange={(e) => setNextEmail(e.target.value)}
          disabled={!canMutate}
        />
      </Field>
      {canMutate && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
    </form>
  );
}

function TaskConcurrencyForm({
  id,
  current,
  canMutate,
}: {
  id: string;
  current: number | null;
  canMutate: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(current == null ? '' : String(current));

  const mutation = useMutation({
    mutationFn: () => {
      const trimmed = value.trim();
      const parsed = trimmed === '' ? null : Math.max(1, Number(trimmed) || 1);
      return setOrgTaskConcurrency(id, parsed);
    },
    onSuccess: (next) => {
      toast.success('Saved');
      setValue(next.max_concurrent_tasks == null ? '' : String(next.max_concurrent_tasks));
      qc.invalidateQueries({ queryKey: qk.org(id) });
    },
    onError: (err) =>
      toast.error('Save failed', err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        mutation.mutate();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 11 }}
    >
      <Field
        id="org-mct"
        label="Max concurrent tasks"
        hint="Empty uses the platform default. Takes effect within a second of saving."
      >
        <Input
          id="org-mct"
          type="number"
          min={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="platform default"
          disabled={!canMutate}
        />
      </Field>
      {canMutate && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </form>
  );
}
